import { useState, useEffect, useLayoutEffect } from "react";
import { useNavigate } from "react-router-dom";

import EditCourseModal from "./EditCourseModal";
import NewCourseModal from "./NewCourseModal";

import TeacherNavbar from "../../components/TeacherNavbar";
import TeacherSidebar from "../../components/Dashboard/TeacherSidebar";

import { getTeacherLibrary } from "../../api/libraries";
import { fetchCourses, updateCourse, createCourse } from "../../api/courses/teachers";
import { buildFormDataForImageUploads } from "../../utils";

import placeholderImage from "../../images/placeholder.png";

import "./index.css";

function EditIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="40" height="40" rx="20" fill="#FCFCFC"/>
      <path d="M21.7471 28.443H28.9997" stroke="#131313" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      <path fillRule="evenodd" clipRule="evenodd" d="M20.78 11.7948C21.5557 10.8678 22.95 10.7319 23.8962 11.4917C23.9485 11.533 25.6295 12.8388 25.6295 12.8388C26.669 13.4672 26.992 14.8031 26.3494 15.8226C26.3153 15.8772 16.8119 27.7645 16.8119 27.7645C16.4958 28.1589 16.0158 28.3918 15.5029 28.3973L11.8635 28.443L11.0435 24.9723C10.9287 24.4843 11.0435 23.9718 11.3597 23.5773L20.78 11.7948Z" stroke="#131313" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M19.0205 14.0009L24.4728 18.188" stroke="#131313" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  )
}

function ViewCourseIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="40" height="40" rx="20" fill="#FCFCFC"/>
      <path d="M27.75 19.7257H12.75" stroke="#131313" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M21.7002 13.7013L27.7502 19.7253L21.7002 25.7503" stroke="#131313" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  )
}

function TeacherCourses({
  courses,
  onEditCourse,
  setCourseBeingEdited,
}) {
  const navigate = useNavigate();
  return (
    courses && courses.map((course) => {
      const {
        id,
        title,
        author_name,
        pretty_created_at,
        hero_image_url,
        author_profile_image_url,
      } = course;
      return (
        <div className="TeacherDashboardCourse">
          <div className="Card">
            <div
              className="CardImg"
              style={{backgroundImage: `url(${hero_image_url})`}}
            >
              <div className="CardActions">
                <div
                  className="CardEditIcon"
                  onClick={() => {
                    onEditCourse(true);
                    setCourseBeingEdited(course)
                  }}
                >
                  <EditIcon />
                </div>
                <div
                  className="CardViewCourse"
                  onClick={() => navigate(`/teacher/courses/${id}`)}
                >
                  <ViewCourseIcon />
                </div>
              </div>
            </div>
            <div className="CardTitle">{title}</div>
            <div className="CardAuthorInfoContainer">
              <div
                style={{
                  backgroundImage: `url(${author_profile_image_url || placeholderImage})`
                }}
                className="CardAurthorProfileImg"
              />
              <div className="CardAuthorInfo">
                <div className="CardAuthorName">
                  <span>By</span>
                  <span>{author_name}</span>
                </div>
                <div className="CardDate">
                  {pretty_created_at.split(".").join("-")}
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    })
  )
}

function TeacherDashboard() {
  const [library, setLibrary] = useState();
  const [courses, setCourses] = useState([]);
  const [editCourse, setEditCourse] = useState(false);
  const [newCourse, setNewCourse] = useState(false);
  const [courseBeingEdited, setCourseBeingEdited] = useState({});
  
  useEffect(async () => {
    try {
      const { library, library: { id } } = await getTeacherLibrary();
      const res = await fetchCourses(id);
      setCourses(res);
      setLibrary(library);
    } catch (err) { // TODO: handle error
      console.log(err)
    }
  }, []);

  useEffect(() => document.body.style.backgroundColor = "#F5F5F5", []);
  useLayoutEffect(() => { return () => document.body.style.backgroundColor = null }, []);

  function updateCourses(val) {
    const newCourses = [...courses];
    newCourses.forEach(({ id }, idx) => {
      if (id === val.id) {
        newCourses[idx] = val;
      }
    })
    setCourses(newCourses);
  }

  function deleteCourse(course) {
    const newCourses = [...courses];
    const idx = newCourses.findIndex(({ id }) => id === course.id);
    newCourses.splice(idx, 1);
    setCourses(newCourses)
  }

  async function onSubmit(values, { resetForm }) {
    const { id } = courseBeingEdited;
    try {
      const formData = buildFormDataForImageUploads(values);
      const res = await updateCourse(id, formData);
      if (res && res.id) {
        updateCourses({ id: id, ...res });
        resetForm();
        
        setEditCourse(false)
        setCourseBeingEdited({})
      }
    } catch (err) {
      console.log(err);
    }
  }

  async function onCreateCourse(values, { resetForm }) {
    try {
      const formData = buildFormDataForImageUploads(values);
      formData.append(`course[library_id]`, library.id);
      const res = await createCourse(formData);
      if (res && res.id) {    
        const arr = [...courses]
        arr.push(res);
        setCourses(arr);
        
        resetForm();
        setNewCourse(false)
        setCourseBeingEdited({})
      }
    } catch (err) {
      console.log(err);
    }
  }

  return (
    <>
      <div className="TeacherDashboardMainContainer">
        <TeacherSidebar />
        <TeacherNavbar createCourse={() => setNewCourse(true)} />
        <div className="TeacherDashboardCoursesContainer">
          <div className="TeacherDashboardCoursesHeader">My Courses</div>
          <div className="TeacherDashboardCourses">
            <TeacherCourses
              courses={courses}
              onEditCourse={setEditCourse}
              setCourseBeingEdited={setCourseBeingEdited}
            />
          </div>
        </div>
      </div>
      {editCourse &&
        <EditCourseModal
          onClose={() => {
            setEditCourse(false)
            setCourseBeingEdited({})
          }}
          onDelete={deleteCourse}
          onSubmit={onSubmit}
          course={courseBeingEdited}
          {...courseBeingEdited}
        />
      }

      {newCourse &&
        <NewCourseModal
          onClose={() => {
            setNewCourse(false);
            setCourseBeingEdited({})
          }}
          onSubmit={onCreateCourse}
          {...courseBeingEdited}
        />
      }
    </>
  )
}

export default TeacherDashboard;
